/**
 * Detect device type.
 *
 * Which method works best depends on your use case:
 *   - For styling: prefer CSS / `useDeviceCssType`.
 *   - For runtime logic that must differ on real touch devices: use the
 *     user-agent flags below.
 *
 * The UA flags are derived once at module load using `parseUserAgent`. We keep
 * the parser pure so tests can drive it with arbitrary UA strings without
 * relying on the global `navigator`.
 */

import { useEffect, useState } from "react"

export interface DeviceFlags {
	isMobile: boolean
	isMobileSafari: boolean
	isIOS: boolean
}

const EMPTY_FLAGS: DeviceFlags = {
	isMobile: false,
	isMobileSafari: false,
	isIOS: false,
}

/**
 * Parse a user-agent string into the small set of flags this app cares about.
 *
 * Notes:
 *   - iPadOS 13+ reports a desktop Safari UA (`Macintosh; Intel Mac OS X`) and
 *     can only be distinguished from a real Mac via `maxTouchPoints`. The
 *     `maxTouchPoints` argument lets the caller pass that in so this stays
 *     pure.
 *   - We deliberately treat iPad as `isMobile` to match `react-device-detect`,
 *     which classified tablets as mobile.
 */
export function parseUserAgent(
	ua: string | null | undefined,
	maxTouchPoints = 0,
): DeviceFlags {
	if (!ua) return EMPTY_FLAGS

	const isIPhone = /iPhone/i.test(ua)
	const isIPod = /iPod/i.test(ua)
	const isIPadUA = /iPad/i.test(ua)
	const isMacintosh = /Macintosh/i.test(ua)
	const isIPadDesktopMode =
		isMacintosh && !/Mobile/i.test(ua) && maxTouchPoints > 1

	const isIOS = isIPhone || isIPod || isIPadUA || isIPadDesktopMode

	const isAndroidMobile = /Android/i.test(ua) && /Mobile/i.test(ua)
	const isAndroidTablet = /Android/i.test(ua) && !/Mobile/i.test(ua)
	const isWindowsPhone = /Windows Phone|IEMobile/i.test(ua)
	const isBlackBerry = /BlackBerry|BB10|PlayBook/i.test(ua)
	const isOtherMobile = /Opera Mini|webOS|Kindle|Silk/i.test(ua)

	const isMobile =
		isIOS ||
		isAndroidMobile ||
		isAndroidTablet ||
		isWindowsPhone ||
		isBlackBerry ||
		isOtherMobile

	// Mobile Safari = WebKit Safari on iOS. Chrome/Firefox/Edge on iOS all use
	// WebKit too but identify with CriOS / FxiOS / EdgiOS — exclude them.
	const isMobileSafari =
		isIOS &&
		/Safari/i.test(ua) &&
		!/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram/i.test(ua)

	return { isMobile, isMobileSafari, isIOS }
}

function readFlags(): DeviceFlags {
	if (typeof navigator === "undefined") return EMPTY_FLAGS
	return parseUserAgent(navigator.userAgent, navigator.maxTouchPoints)
}

// Exposed as functions (not module-level constants) so each call reads the
// current `navigator` rather than caching a value at module load. This keeps
// device detection request-scoped and avoids leaking UA info across requests
// in any environment where this module is reused.
export const isMobile = () => readFlags().isMobile
export const isMobileSafari = () => readFlags().isMobileSafari
export const isIOS = () => readFlags().isIOS

const useDeviceCssType = (breakpoint = 768) => {
	const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint)

	useEffect(() => {
		const query = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
		setIsMobile(query.matches)
		query.addEventListener("change", handler)
		return () => query.removeEventListener("change", handler)
	}, [breakpoint])

	return isMobile ? "mobile" : "desktop"
}

const useDeviceAgentType = () => {
	return isMobile() ? "mobile" : "desktop"
}

export { useDeviceCssType, useDeviceAgentType }
